/**
 * PrioritySelector - Seletor visual de prioridade com badges interativos
 * Design premium com cores pastéis e feedback visual
 */
import React from 'react';
import { Flag } from 'lucide-react';
import { cn } from '@/lib/utils';
import { DealPriority } from '@/hooks/useCrmDeals';

/**
 * Props do componente PrioritySelector
 */
interface PrioritySelectorProps {
  value: DealPriority;
  onChange: (value: DealPriority) => void;
  disabled?: boolean;
  /** Modo compacto: botões menores, labels abreviados, sem quebra de linha */
  compact?: boolean;
}

/**
 * Configuração de prioridades com labels normais e compactos
 */
const priorities: { 
  value: DealPriority; 
  label: string; 
  shortLabel: string;
  color: string; 
  bgColor: string; 
  borderColor: string;
}[] = [
  { 
    value: 'low', 
    label: 'Baixa',
    shortLabel: 'Baixa',
    color: 'text-emerald-600 dark:text-emerald-400',
    bgColor: 'bg-emerald-50 dark:bg-emerald-950/50',
    borderColor: 'border-emerald-200 dark:border-emerald-800'
  },
  { 
    value: 'medium', 
    label: 'Média',
    shortLabel: 'Média',
    color: 'text-blue-600 dark:text-blue-400',
    bgColor: 'bg-blue-50 dark:bg-blue-950/50',
    borderColor: 'border-blue-200 dark:border-blue-800'
  },
  { 
    value: 'high', 
    label: 'Alta',
    shortLabel: 'Alta',
    color: 'text-amber-600 dark:text-amber-400',
    bgColor: 'bg-amber-50 dark:bg-amber-950/50',
    borderColor: 'border-amber-200 dark:border-amber-800'
  },
  { 
    value: 'urgent', 
    label: 'Urgente',
    shortLabel: 'Urg.',
    color: 'text-rose-600 dark:text-rose-400',
    bgColor: 'bg-rose-50 dark:bg-rose-950/50',
    borderColor: 'border-rose-200 dark:border-rose-800'
  },
];

/**
 * Componente PrioritySelector
 * Seletor visual de prioridade com badges interativos
 */
export function PrioritySelector({ value, onChange, disabled, compact = false }: PrioritySelectorProps) {
  return (
    <div className={cn("flex gap-1", compact ? "flex-nowrap" : "flex-wrap gap-1.5")}>
      {priorities.map((priority) => {
        const isSelected = value === priority.value;
        return (
          <button
            key={priority.value}
            type="button"
            disabled={disabled}
            onClick={() => onChange(priority.value)}
            className={cn(
              "inline-flex items-center rounded-lg font-medium shrink-0",
              "border transition-all duration-200",
              "focus:outline-none focus:ring-2 focus:ring-primary/20",
              "disabled:opacity-50 disabled:cursor-not-allowed",
              // Tamanho: compacto ou padrão
              compact 
                ? "px-1.5 py-1 text-[10px] gap-1" 
                : "px-2.5 py-1.5 text-xs gap-1.5",
              isSelected
                ? cn(priority.bgColor, priority.borderColor, priority.color, "ring-2 ring-primary/30")
                : "bg-background border-border/50 text-muted-foreground hover:bg-muted/50"
            )}
          >
            <Flag className={cn(
              compact ? "w-2.5 h-2.5" : "w-3 h-3",
              isSelected ? priority.color : "text-muted-foreground"
            )} />
            {compact ? priority.shortLabel : priority.label}
          </button>
        );
      })}
    </div>
  );
}
